// Justin's Launch reports 2 and 3 (21 Oct 2026): lead generation by portfolio line and by client, and each client's
// status, red/yellow/green. public.dashboard_report_lead_generation, dashboard_report_client_status,
// dashboard_save_client_health and dashboard_save_website_line (rnrinfra 20261021130000_launch_reports.sql).
// Admins and super admins only; spend linked to no client comes back for super admins only.

// Report 2: lead generation -------------------------------------------------------------------------------------------
export type LineRow = {
  line_key: string; label: string; brand: string; sites: number; sites_with_client: number;
  leads: number; qualified: number; paid: number; paid_qualified: number; organic: number; calls: number; forms: number;
  spend: number; cpl: number | null; cpql: number | null; projected: number | null;
  prior: { leads: number; qualified: number; spend: number };
};
export type LeadGenClient = {
  business_id: string; client_name: string; manager: string | null; stage: string; relationship_status: string; lines: string[];
  goal: number | null; expected: number | null; projected: number | null; pace: 'met' | 'ahead' | 'on_track' | 'behind' | 'no_goal';
  paid: { leads: number; qualified: number; calls: number; forms: number; spend: number; google: number; meta: number; cpl: number | null; cpql: number | null };
  organic: { leads: number; qualified: number; calls: number; forms: number };
  total: { leads: number; qualified: number; calls: number; forms: number; untracked: number };
  prior: { leads: number; qualified: number; paid: number; spend: number };
  trend_pct: number | null;
};
export type LeadGenWebsite = { website_id: number; site: string | null; line_key: string; automatic: boolean; client_name: string | null; leads: number };
export type LeadGeneration = {
  month: string; prior_month: string; timezone: string; generated_at: string; share_of_month: number; last_synced_on: string | null;
  lines: LineRow[]; clients: LeadGenClient[]; websites: LeadGenWebsite[];
  totals: {
    leads: number; qualified: number; paid: number; paid_qualified: number; organic: number; calls: number; forms: number; untracked: number;
    prior_leads: number; prior_qualified: number; spend: number; prior_spend: number;
  };
  /** null for admins: spend linked to no client belongs to no agency. */
  unlinked_spend: { google: number; meta: number } | null;
  ad_set_split: { meta_spend: number; meta_spend_split: number };
};

/** Change against last month, in percent, from this month's leads run to the end of the month; null without last month. */
export function trendPct(current: number, prior: number, share: number): number | null {
  if (prior <= 0 || share <= 0) return null;
  return Math.round((100 * (current / share - prior)) / prior);
}
export const trendLabel = (pct: number | null) => (pct === null ? '—' : pct === 0 ? 'Same as last month' : `${pct > 0 ? '+' : '−'}${Math.abs(pct)}% vs last month`);
export const trendTone = (pct: number | null) => (pct === null || Math.abs(pct) < 10 ? 'neutral' : pct > 0 ? 'green' : 'red');

/** "31%": part of a whole, rounded; a dash with nothing to divide. */
export const percent = (part: number, whole: number) => (whole > 0 ? `${Math.round((100 * part) / whole)}%` : '—');

/** A line's name, as the report labels it; "Other services" or the key for one it does not list. */
export function lineName(lines: Pick<LineRow, 'line_key' | 'label'>[], key: string) {
  return lines.find(l => l.line_key === key)?.label ?? (key === 'other' ? 'Other services' : key === 'unknown' ? 'No website or campaign' : key);
}

/** The lines worth a row: any with websites, leads or spend this month or last, unless every line is asked for. */
export const visibleLines = (lines: LineRow[], all: boolean) =>
  all ? lines : lines.filter(l => l.sites + l.leads + l.prior.leads > 0 || l.spend + l.prior.spend > 0);

/** The lines a website can be put in: every listed line but "No website or campaign". */
export const lineChoices = (lines: LineRow[]) => lines.filter(l => l.line_key !== 'unknown').map(l => ({ key: l.line_key, label: l.label }));

export type ClientSort = 'leads' | 'qualified' | 'spend' | 'cpql' | 'name';
export const clientSortLabels: Record<ClientSort, string> = { leads: 'Most leads', qualified: 'Most qualified', spend: 'Most spend', cpql: 'Costliest qualified lead', name: 'Name' };
export function sortClients(clients: LeadGenClient[], by: ClientSort): LeadGenClient[] {
  const key: Record<ClientSort, (c: LeadGenClient) => number> = {
    leads: c => c.total.leads, qualified: c => c.total.qualified, spend: c => c.paid.spend, cpql: c => c.paid.cpql ?? -1, name: () => 0,
  };
  return [...clients].sort((a, b) => key[by](b) - key[by](a) || a.client_name.localeCompare(b.client_name));
}

/** Websites matching the search (site or client) and, when one is chosen, the line. */
export function filterWebsites(websites: LeadGenWebsite[], query: string, line: string) {
  const q = query.trim().toLowerCase();
  return websites.filter(w => (!line || w.line_key === line) && (!q || `${w.site ?? ''} ${w.client_name ?? ''}`.toLowerCase().includes(q)));
}

// Report 3: client status ---------------------------------------------------------------------------------------------
export type Health = 'green' | 'yellow' | 'red';
export type PaymentStatus = 'unknown' | 'current' | 'repaying' | 'overdue';
export type StatusClient = {
  business_id: string; client_name: string; manager: string | null; relationship_status: string; stage: string; lines: string[];
  start_on: string | null; months: number | null; billing_model: string | null; monthly_fee: number | null; price_per_lead: number | null;
  mix: 'paid_and_organic' | 'organic'; leads: number; qualified: number; goal: number | null; expected: number | null; last_lead_at: string | null;
  spend_7: number; spend_30: number;
  payment: { status: PaymentStatus; balance_due: number | null; note: string | null; delivery_scope: string };
  health: { satisfaction: number | null; at_risk: boolean | null; at_risk_now: boolean; considering_cancel: boolean; note: string | null; revision: number; updated_at: string | null };
  feedback: { rated: number; good: number; okay: number; bad: number };
  cancelled_on: string | null; cancel_reason: string | null;
  status: Health; red: string[]; yellow: string[]; gaps: string[];
};
export type ClientStatusReport = {
  month: string; timezone: string; generated_at: string; share_of_month: number;
  counts: { green: number; yellow: number; red: number; unpaid_still_buying: number; payment_unknown: number };
  clients: StatusClient[];
};

export const healthLabels: Record<Health, string> = { green: 'Healthy', yellow: 'At risk', red: 'Payment or cancellation issue' };
export const paymentLabels: Record<PaymentStatus, string> = { unknown: 'Not recorded', current: 'Current', repaying: 'Repaying', overdue: 'Overdue' };
export const mixLabels: Record<StatusClient['mix'], string> = { paid_and_organic: 'Paid + organic', organic: 'Organic only' };

const healthRank: Record<Health, number> = { red: 0, yellow: 1, green: 2 };
/** Red first, then yellow, then green; by name within each. */
export const sortByHealth = (clients: StatusClient[]) =>
  [...clients].sort((a, b) => healthRank[a.status] - healthRank[b.status] || a.client_name.localeCompare(b.client_name));
export const filterByHealth = (clients: StatusClient[], status: Health | 'all') => (status === 'all' ? clients : clients.filter(c => c.status === status));

/** "6.1 months", or "3 weeks" under a month; a dash before the client paid. */
export function tenure(months: number | null): string {
  if (months === null) return '—';
  if (months < 1) return `${Math.max(0, Math.round(months * 4.35))} weeks`;
  return `${months.toFixed(1)} months`;
}

/** "$1,500/mo", "$45/lead", "$900/mo + $30/lead"; null without a fee. */
export function feeLabel(c: Pick<StatusClient, 'monthly_fee' | 'price_per_lead'>): string | null {
  const parts = [c.monthly_fee !== null ? `${usd(c.monthly_fee)}/mo` : '', c.price_per_lead !== null ? `${usd(c.price_per_lead)}/lead` : ''].filter(Boolean);
  return parts.length ? parts.join(' + ') : null;
}
const usd = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: n % 1 ? 2 : 0 });

/** The form for a client's health and payment: strings as the inputs hold them. */
export type HealthDraft = {
  satisfaction: '' | '1' | '2' | '3' | '4' | '5'; at_risk: 'auto' | 'yes' | 'no'; considering_cancel: boolean; note: string;
  receivable_status: PaymentStatus; balance_due: string;
};
export function toDraft(c: StatusClient): HealthDraft {
  return {
    satisfaction: c.health.satisfaction === null ? '' : (String(c.health.satisfaction) as HealthDraft['satisfaction']),
    at_risk: c.health.at_risk === null ? 'auto' : c.health.at_risk ? 'yes' : 'no',
    considering_cancel: c.health.considering_cancel, note: c.health.note ?? '',
    receivable_status: c.payment.status, balance_due: c.payment.balance_due === null ? '' : String(c.payment.balance_due),
  };
}

/** "$1,250.50" as a spreadsheet or a person writes it, to "1250.50"; null for blank; undefined when it is not an amount. */
export function readAmount(text: string): string | null | undefined {
  const t = text.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!t) return null;
  return /^\d{1,9}(\.\d{1,2})?$/.test(t) ? t : undefined;
}

/**
 * The save request (dashboard_save_client_health): only what changed, with the revision the form was opened at. An
 * error instead when the balance is not an amount; null when nothing changed.
 */
export function healthRequest(c: StatusClient, draft: HealthDraft): { request: Record<string, unknown> } | { error: string } | null {
  const was = toDraft(c), request: Record<string, unknown> = { business_id: c.business_id, expected_revision: c.health.revision };
  if (draft.satisfaction !== was.satisfaction) request.satisfaction = draft.satisfaction === '' ? null : Number(draft.satisfaction);
  if (draft.at_risk !== was.at_risk) request.at_risk = draft.at_risk === 'auto' ? null : draft.at_risk === 'yes';
  if (draft.considering_cancel !== was.considering_cancel) request.considering_cancel = draft.considering_cancel;
  if (draft.note.trim() !== was.note.trim()) request.note = draft.note.trim();
  if (draft.receivable_status !== was.receivable_status) request.receivable_status = draft.receivable_status;
  const amount = readAmount(draft.balance_due);
  if (amount === undefined) return { error: 'Enter the balance owed as an amount in dollars, or leave it blank.' };
  const before = readAmount(was.balance_due);
  if ((amount === null) !== (before == null) || (amount !== null && Number(amount) !== Number(before))) request.balance_due = amount;
  return Object.keys(request).length > 2 ? { request } : null;
}
